import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Configurações do Google OAuth
const GOOGLE_CLIENT_ID = '574024194800-cr3d2j004b581i6tst2i2krtuehqchgb.apps.googleusercontent.com';
const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/calendar.events';
// const REDIRECT_URI = window.location.origin + '/auth/callback'; // Não usado atualmente

/**
 * Inicializa o Google Calendar API
 */
export const initGoogleCalendar = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        resolve(window.google);
      } else {
        reject(new Error('Failed to load Google Identity Services'));
      }
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

/**
 * Conecta ao Google Calendar (OAuth flow)
 */
export const connectGoogleCalendar = async (userId) => {
  try {
    await initGoogleCalendar();
    
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: async (response) => {
        if (response.access_token) {
          // Salvar token no Firestore
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            googleCalendar: {
              connected: true,
              accessToken: response.access_token,
              expiresAt: new Date(Date.now() + (response.expires_in * 1000)),
              connectedAt: new Date(),
            }
          });
          
          return { success: true, message: 'Google Calendar conectado com sucesso!' };
        } else {
          throw new Error('Falha ao obter token de acesso');
        }
      },
      error_callback: (error) => {
        console.error('OAuth error:', error);
        throw error;
      }
    });

    client.requestAccessToken();
    
  } catch (error) {
    console.error('Error connecting Google Calendar:', error);
    return { 
      success: false, 
      error: error.message || 'Erro ao conectar com Google Calendar' 
    };
  }
};

/**
 * Desconecta do Google Calendar
 */
export const disconnectGoogleCalendar = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      googleCalendar: {
        connected: false,
        accessToken: null,
        expiresAt: null,
        connectedAt: null,
      }
    });
    
    return { success: true, message: 'Google Calendar desconectado' };
  } catch (error) {
    console.error('Error disconnecting:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verifica se o token ainda é válido
 */
const isTokenValid = (expiresAt) => {
  if (!expiresAt) return false;
  const expiryDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
  return expiryDate > new Date();
};

/**
 * Obtém o token de acesso válido
 */
const getValidAccessToken = async (userId) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  const userData = userDoc.data();
  
  if (!userData?.googleCalendar?.connected) {
    throw new Error('Google Calendar não está conectado');
  }
  
  if (!isTokenValid(userData.googleCalendar.expiresAt)) {
    throw new Error('Token expirado. Reconecte o Google Calendar');
  }
  
  return userData.googleCalendar.accessToken;
};

/**
 * Cria evento no Google Calendar
 */
export const createCalendarEvent = async (userId, appointment, barbershop) => {
  try {
    const accessToken = await getValidAccessToken(userId);
    
    const event = {
      summary: `${appointment.serviceName} - ${appointment.clientName}`,
      description: `
Agendamento Barberize
Cliente: ${appointment.clientName}
Email: ${appointment.clientEmail || 'Não informado'}
Serviço: ${appointment.serviceName}
Valor: R$ ${appointment.servicePrice || 'A combinar'}
${appointment.notes ? '\nObservações: ' + appointment.notes : ''}
      `.trim(),
      location: barbershop.address,
      start: {
        dateTime: new Date(`${appointment.date}T${appointment.time}`).toISOString(),
        timeZone: 'America/Sao_Paulo',
      },
      end: {
        dateTime: new Date(
          new Date(`${appointment.date}T${appointment.time}`).getTime() + 60 * 60 * 1000
        ).toISOString(),
        timeZone: 'America/Sao_Paulo',
      },
      attendees: appointment.clientEmail ? [
        { email: appointment.clientEmail }
      ] : [],
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'email', minutes: 24 * 60 },
          { method: 'popup', minutes: 30 },
        ],
      },
    };

    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/calendars/primary/events',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Erro ao criar evento');
    }

    const data = await response.json();
    
    return { 
      success: true, 
      eventId: data.id,
      htmlLink: data.htmlLink,
      message: 'Evento criado no Google Calendar' 
    };
    
  } catch (error) {
    console.error('Error creating calendar event:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Atualiza evento no Google Calendar
 */
export const updateCalendarEvent = async (userId, eventId, appointment, barbershop) => {
  try {
    const accessToken = await getValidAccessToken(userId);
    
    const event = {
      summary: `${appointment.serviceName} - ${appointment.clientName}`,
      description: `
Agendamento Barberize (${appointment.status === 'confirmed' ? 'CONFIRMADO' : 'PENDENTE'})
Cliente: ${appointment.clientName}
Email: ${appointment.clientEmail || 'Não informado'}
Serviço: ${appointment.serviceName}
Valor: R$ ${appointment.servicePrice || 'A combinar'}
${appointment.notes ? '\nObservações: ' + appointment.notes : ''}
      `.trim(),
      location: barbershop.address,
      start: {
        dateTime: new Date(`${appointment.date}T${appointment.time}`).toISOString(),
        timeZone: 'America/Sao_Paulo',
      },
      end: {
        dateTime: new Date(
          new Date(`${appointment.date}T${appointment.time}`).getTime() + 60 * 60 * 1000
        ).toISOString(),
        timeZone: 'America/Sao_Paulo',
      },
    };

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      }
    );

    if (!response.ok) {
      throw new Error('Erro ao atualizar evento');
    }

    return { success: true, message: 'Evento atualizado no Google Calendar' };
    
  } catch (error) {
    console.error('Error updating calendar event:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deleta evento do Google Calendar
 */
export const deleteCalendarEvent = async (userId, eventId) => {
  try {
    const accessToken = await getValidAccessToken(userId);

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok && response.status !== 404) {
      throw new Error('Erro ao deletar evento');
    }

    return { success: true, message: 'Evento removido do Google Calendar' };
    
  } catch (error) {
    console.error('Error deleting calendar event:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verifica status da conexão
 */
export const checkCalendarConnection = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData?.googleCalendar?.connected) {
      return { connected: false };
    }
    
    const isValid = isTokenValid(userData.googleCalendar.expiresAt);
    
    return {
      connected: userData.googleCalendar.connected,
      tokenValid: isValid,
      connectedAt: userData.googleCalendar.connectedAt,
    };
  } catch (error) {
    console.error('Error checking connection:', error);
    return { connected: false, error: error.message };
  }
};
